import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ListChecks, Plus, X, ChevronDown, ChevronRight } from 'lucide-react';

/** Small "2/5" progress chip shown on task rows that have sub-tasks. */
export function SubtaskProgressChip({ done, total, expanded, onClick }) {
  if (!total) return null;
  const complete = done >= total;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 transition-colors ${
        complete
          ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
          : 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-500/30'
      }`}
      data-testid="subtask-progress-chip"
      title={expanded === undefined ? undefined : expanded ? 'Hide sub-tasks' : 'Show sub-tasks'}
    >
      <ListChecks className="w-2.5 h-2.5" />
      {done}/{total}
      {expanded !== undefined && (expanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />)}
    </button>
  );
}

/** Read/check-only checklist rendered under an expanded task row. */
export function SubtaskChecklist({ items, checkedIds, onToggle, className = '' }) {
  if (!items?.length) return null;
  return (
    <div className={`ml-8 mr-2 mb-1.5 space-y-0.5 ${className}`} data-testid="subtask-checklist">
      {items.map(sub => {
        const checked = checkedIds.includes(sub.id);
        return (
          <label
            key={sub.id}
            className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-white/[0.04] cursor-pointer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`subtask-row-${sub.id}`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => onToggle(sub.id, !!v)}
              className="h-3.5 w-3.5 border-slate-300 dark:border-slate-600 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
              data-testid={`subtask-check-${sub.id}`}
            />
            <span className={`text-xs ${checked ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300'}`}>
              {sub.title}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Full editor used inside Add/Edit Task dialogs: list + add input + remove.
 * When showChecks is true (edit mode), each row also has its checkbox for the
 * current occurrence.
 */
export function SubtaskEditor({ items, onChange, checkedIds = [], onToggleCheck, showChecks = false, note, hideHeader = false }) {
  const [draft, setDraft] = useState('');

  const addItem = () => {
    const title = draft.trim();
    if (!title) return;
    onChange([...(items || []), { id: `st_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, title }]);
    setDraft('');
  };

  return (
    <div data-testid="subtask-editor">
      {!hideHeader && (
      <div className="flex items-center gap-1.5 mb-1.5">
        <ListChecks className="w-3 h-3 text-slate-400" />
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Sub-tasks</span>
        {items?.length > 0 && (
          <span className="text-[10px] text-slate-400">{checkedIds.filter(id => (items || []).some(i => i.id === id)).length}/{items.length}</span>
        )}
      </div>
      )}
      {(items || []).length > 0 && (
        <div className="space-y-0.5 mb-1.5">
          {items.map(sub => (
            <div key={sub.id} className="group/sub flex items-center gap-2 py-1 px-2 rounded-lg bg-slate-50 dark:bg-white/[0.04]" data-testid={`subtask-editor-row-${sub.id}`}>
              {showChecks && (
                <Checkbox
                  checked={checkedIds.includes(sub.id)}
                  onCheckedChange={(v) => onToggleCheck?.(sub.id, !!v)}
                  className="h-3.5 w-3.5 data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                  data-testid={`subtask-editor-check-${sub.id}`}
                />
              )}
              <span className={`flex-1 text-xs truncate ${showChecks && checkedIds.includes(sub.id) ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300'}`}>
                {sub.title}
              </span>
              <button
                onClick={() => onChange(items.filter(i => i.id !== sub.id))}
                className="text-slate-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400 transition-colors"
                data-testid={`subtask-remove-${sub.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <Input
          placeholder="Add a sub-task…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); addItem(); } }}
          className="h-8 text-xs"
          data-testid="subtask-add-input"
        />
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 flex-shrink-0" onClick={addItem} data-testid="subtask-add-btn">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      {note && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">{note}</p>}
    </div>
  );
}
